/**
 * TanStack Query hooks for the configurations API.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchConfigurations,
  createConfiguration,
  updateConfiguration,
  deleteConfiguration,
  type Configuration,
  type ConfigurationCreate,
  type ConfigurationUpdate,
} from "../api/configurations";

export function useConfigurations(projectId: string) {
  return useQuery<Configuration[]>({
    queryKey: ["configurations", projectId],
    queryFn: () => fetchConfigurations(projectId),
    enabled: !!projectId,
  });
}

export function useCreateConfiguration(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConfigurationCreate) => createConfiguration(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["configurations", projectId] }),
  });
}

export function useUpdateConfiguration(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ConfigurationUpdate }) =>
      updateConfiguration(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["configurations", projectId] }),
  });
}

export function useDeleteConfiguration(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteConfiguration(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["configurations", projectId] }),
  });
}
